import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import toast from "react-hot-toast";
import Cookies from 'js-cookie';
import useLoginState from "../../hooks/useLoginState";
import EmailIcon from '@mui/icons-material/EmailOutlined';
import LockIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOffOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LanguageSwitcher from "../../components/LanguageSwitcher";
import { postResendEmail } from "../../services/postResendEmail";
import { loginGoogle } from "../../services/auth";
import { AuthContext } from "../../contexts/auth";
import ForgotPasswordModal from "../../components/forgotPasswordModal";

const ADMIND_LOGO = "https://admindaiacademy.s3.eu-central-1.amazonaws.com/Random+Sheet/Loghi+AdMind/ADMINDLogoNegative.png";
const ADMIND_LOGO_POS = "https://admindaiacademy.s3.eu-central-1.amazonaws.com/Random+Sheet/Loghi+AdMind/AdMindLogoPos.svg";

const COOLDOWN_SECONDS = 60;

const LOGIN_KEYFRAMES = `
@keyframes shimmer-login {
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
}
@keyframes pulse-dot {
  0%, 100% { box-shadow: 0 0 0 0 rgba(0,255,136,0.5); }
  50% { box-shadow: 0 0 0 5px rgba(0,255,136,0); }
}
@keyframes blink-cursor {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}
@keyframes fade-up-login {
  0% { opacity: 0; transform: translateY(16px); }
  100% { opacity: 1; transform: translateY(0); }
}
`;

const AGENT_STEP_ICONS = ['🎯', '✍️', '🖼️', '📅', '📊', '✅'];

function LoginAgentDemo() {
    const { t } = useTranslation();
    const stepLabels = t('landing.agent_steps', { returnObjects: true });
    const STEPS = stepLabels.map((label, i) => ({ icon: AGENT_STEP_ICONS[i], label }));
    const [stepIndex, setStepIndex] = useState(0);
    const [text, setText] = useState('');
    const [typing, setTyping] = useState(true);

    useEffect(() => {
        const full = STEPS[stepIndex].label;
        let i = 0;
        let nextTimeout = null;
        setText('');
        setTyping(true);
        const interval = setInterval(() => {
            i++;
            setText(full.slice(0, i));
            if (i >= full.length) {
                clearInterval(interval);
                setTyping(false);
                nextTimeout = setTimeout(() => {
                    setStepIndex(s => (s + 1) % STEPS.length);
                }, 1400);
            }
        }, 38);
        return () => { clearInterval(interval); if (nextTimeout) clearTimeout(nextTimeout); };
    }, [stepIndex]);

    return (
        <Box sx={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(112,18,128,0.25)',
            borderRadius: '16px',
            p: 2.5,
            backdropFilter: 'blur(20px)',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#00ff88', animation: 'pulse-dot 2s infinite' }} />
                <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)', letterSpacing: 2, textTransform: 'uppercase' }}>
                    {t('landing.agent_running')}
                </Typography>
            </Box>
            {STEPS.map((step, i) => (
                <Box key={i} sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    opacity: i < stepIndex ? 1 : i === stepIndex ? 1 : 0.2,
                    transition: 'opacity 0.4s ease',
                    mb: i < STEPS.length - 1 ? 0.9 : 0,
                }}>
                    <Typography sx={{ fontSize: '0.9rem', flexShrink: 0 }}>{step.icon}</Typography>
                    <Typography sx={{
                        fontSize: '0.79rem',
                        color: i < stepIndex ? 'rgba(255,255,255,0.45)' : i === stepIndex ? '#fff' : 'rgba(255,255,255,0.2)',
                        fontFamily: 'monospace',
                        textDecoration: i < stepIndex ? 'line-through' : 'none',
                    }}>
                        {i === stepIndex ? text : step.label}
                        {i === stepIndex && typing && (
                            <Box component="span" sx={{ borderRight: '2px solid #701280', ml: 0.3, animation: 'blink-cursor 1s infinite' }}>&nbsp;</Box>
                        )}
                        {i < stepIndex && (
                            <Box component="span" sx={{ ml: 1, color: '#00ff88', fontSize: '0.72rem' }}>✓</Box>
                        )}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

function Login() {
    const { email, password, submit, remember, errors, notVerified } = useLoginState();
    const navigate = useNavigate();
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const { signIn, signed } = React.useContext(AuthContext);
    const [showPass, setShowPass] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
    const [forgotOpen, setForgotOpen] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);

    const [resending, setResending] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const cooldownRef = useRef(null);

    const handleGoogleTokenResponse = async (tokenResponse) => {
        if (!tokenResponse?.access_token) return;
        setGoogleLoading(true);
        try {
            const data = await loginGoogle(tokenResponse.access_token);
            if (data?.user?.verified !== false) {
                await signIn(data, false);
                navigate('/dashboard');
            }
        } catch (err) {
            toast.error(err.message || 'Errore login Google');
        } finally {
            setGoogleLoading(false);
        }
    };

    const handleGoogleClick = () => {
        if (!GOOGLE_CLIENT_ID) {
            toast.error('Google login non configurato');
            return;
        }
        const loadAndRequest = () => {
            const client = window.google.accounts.oauth2.initTokenClient({
                client_id: GOOGLE_CLIENT_ID,
                scope: 'openid email profile',
                callback: handleGoogleTokenResponse,
            });
            client.requestAccessToken({ prompt: 'select_account' });
        };

        if (window.google?.accounts?.oauth2) {
            loadAndRequest();
        } else {
            const scriptId = 'google-gsi-script';
            let script = document.getElementById(scriptId);
            if (!script) {
                script = document.createElement('script');
                script.id = scriptId;
                script.src = 'https://accounts.google.com/gsi/client';
                script.async = true;
                document.head.appendChild(script);
            }
            script.onload = loadAndRequest;
        }
    };

    useEffect(() => {
        Cookies.remove("airton@data");
        const params = new URLSearchParams(location.search);
        const langParam = params.get('lang');
        if (langParam && ['it', 'en', 'es', 'pt'].includes(langParam)) {
            i18n.changeLanguage(langParam);
            localStorage.setItem('admind_lang', langParam);
        }
        if (params.get('verified') === '1') {
            setIsVerified(true);
            window.history.replaceState({}, '', '/login');
        }

        if (GOOGLE_CLIENT_ID) {
            const scriptId = 'google-gsi-script';
            if (!document.getElementById(scriptId)) {
                const script = document.createElement('script');
                script.id = scriptId;
                script.src = 'https://accounts.google.com/gsi/client';
                script.async = true;
                document.head.appendChild(script);
            }
        }
    }, []);

    useEffect(() => {
        if (signed) {
            navigate('/dashboard');
        }
    }, [navigate, signed]);

    useEffect(() => {
        return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
    }, []);

    const startCooldown = () => {
        setCooldown(COOLDOWN_SECONDS);
        cooldownRef.current = setInterval(() => {
            setCooldown(prev => {
                if (prev <= 1) {
                    clearInterval(cooldownRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleResend = async () => {
        if (cooldown > 0 || resending) return;
        const userEmail = notVerified.email || localStorage.getItem("userEmail");
        if (!userEmail) return;
        setResending(true);
        const response = await postResendEmail({ email: userEmail });
        setResending(false);
        if (response.type === 'success') {
            toast.success(t('registration.email_resent') || 'Email inviata di nuovo!');
            startCooldown();
        } else {
            toast.error(response.data?.detail || 'Errore nel reinvio email');
            startCooldown();
        }
    };

    return (
        <>
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            overflow: 'hidden',
            position: 'relative',
            backgroundColor: '#080810',
        }}>
            {/* Left panel — dark, branded, landing-style */}
            <Box sx={{
                display: { xs: 'none', lg: 'flex' },
                flex: 1,
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                px: 6, py: 6,
                position: 'relative',
                background: '#080810',
                overflow: 'hidden',
            }}>
                <style>{LOGIN_KEYFRAMES}</style>
                <Box sx={{ position: 'absolute', top: '-20%', left: '-15%', width: '70%', height: '70%', background: 'radial-gradient(ellipse, rgba(112,18,128,0.22) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: '-10%', right: '-10%', width: '55%', height: '55%', background: 'radial-gradient(ellipse, rgba(255,107,53,0.10) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: '20%', left: '10%', width: '40%', height: '40%', background: 'radial-gradient(ellipse, rgba(112,18,128,0.10) 0%, transparent 70%)', filter: 'blur(50px)', pointerEvents: 'none' }} />

                <Box sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 520 }}>

                    {/* Logo */}
                    <Box sx={{ mb: 5 }}>
                        <img src={ADMIND_LOGO} alt="Admind" style={{ height: 40 }} />
                    </Box>

                    {/* Badge — landing style */}
                    <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 1,
                        background: 'rgba(112,18,128,0.15)', border: '1px solid rgba(112,18,128,0.4)',
                        borderRadius: '100px', px: 2, py: 0.75, mb: 3,
                    }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#00ff88', animation: 'pulse-dot 2s infinite' }} />
                        <Typography sx={{ fontSize: '0.71rem', color: 'rgba(255,255,255,0.8)', letterSpacing: 1.2, textTransform: 'uppercase', fontWeight: 600 }}>
                            {t('auth.agentic_badge')}
                        </Typography>
                    </Box>

                    {/* Hero title with shimmer */}
                    <Typography variant="h1" sx={{ fontSize: { lg: '2.8rem', xl: '3.2rem' }, fontWeight: 900, color: '#fff', mb: 1, lineHeight: 1.08, letterSpacing: '-1.5px' }}>
                        {t('auth.hero_title1')}<br />
                        <Box component="span" sx={{
                            background: 'linear-gradient(135deg, #701280 0%, #C850C0 50%, #FF6B35 100%)',
                            backgroundSize: '200% auto',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            animation: 'shimmer-login 4s linear infinite',
                        }}>
                            {t('auth.hero_title2')}
                        </Box>
                    </Typography>

                    {/* Subtitle */}
                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', mb: 3.5, fontSize: '0.97rem', lineHeight: 1.7 }}>
                        {t('auth.hero_subtitle')}
                    </Typography>

                    {/* Agent typing demo */}
                    <LoginAgentDemo />

                    {/* Disclaimer */}
                    <Typography sx={{ mt: 3, fontSize: '0.75rem', color: 'rgba(255,255,255,0.25)', letterSpacing: 0.3 }}>
                        {t('landing.disclaimer')}
                    </Typography>
                </Box>
            </Box>

            {/* Right panel — white form */}
            <Box sx={{
                width: { xs: '100%', lg: '480px' },
                flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                p: { xs: 3, sm: 6 },
                position: 'relative',
                background: '#FAFAF8',
                borderLeft: { lg: '1px solid rgba(255,255,255,0.07)' },
            }}>
                {/* Language switcher top-right */}
                <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
                    <LanguageSwitcher dark={false} />
                </Box>

                <Box sx={{
                    width: '100%', maxWidth: 420,
                    background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.08)',
                    borderRadius: '24px', p: { xs: 3, sm: 4 },
                    boxShadow: '0 8px 40px rgba(0,0,0,0.10)',
                }}>
                    <Box sx={{ display: { xs: 'flex', lg: 'none' }, alignItems: 'center', gap: 1.5, mb: 3 }}>
                        <img src={ADMIND_LOGO_POS} alt="Admind" style={{ height: 34 }} />
                    </Box>

                    {/* ✅ Email verified success banner */}
                    {isVerified && (
                        <Box sx={{
                            display: 'flex', alignItems: 'flex-start', gap: 1.5,
                            background: 'linear-gradient(135deg, rgba(0,200,120,0.08), rgba(0,200,120,0.04))',
                            border: '1px solid rgba(0,180,100,0.3)',
                            borderRadius: '12px', p: 2, mb: 3,
                        }}>
                            <CheckCircleOutlineIcon sx={{ color: '#00b864', fontSize: '1.3rem', mt: 0.1, flexShrink: 0 }} />
                            <Box>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#006b3c', mb: 0.3 }}>
                                    {t('login.email_verified_title') || 'Email confermata!'}
                                </Typography>
                                <Typography sx={{ fontSize: '0.82rem', color: '#338855', lineHeight: 1.5 }}>
                                    {t('login.email_verified_body') || 'Il tuo indirizzo email è stato confermato con successo. Inserisci le tue credenziali per accedere alla piattaforma.'}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* ⚠️ Email not verified inline banner */}
                    {notVerified.value && (
                        <Box sx={{
                            background: 'linear-gradient(135deg, rgba(112,18,128,0.07), rgba(200,80,192,0.04))',
                            border: '1px solid rgba(112,18,128,0.25)',
                            borderRadius: '14px', p: 2.5, mb: 3, textAlign: 'center',
                        }}>
                            <MarkEmailReadOutlinedIcon sx={{ color: '#701280', fontSize: '2rem', mb: 1 }} />
                            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1C1C28', mb: 0.5 }}>
                                {t('login.email_not_verified_title') || 'Conferma il tuo indirizzo email'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.82rem', color: '#6B6B7B', lineHeight: 1.6, mb: 1.5 }}>
                                {t('login.email_not_verified_body') || 'Per favore, conferma il tuo indirizzo cliccando sul link che ti abbiamo inviato via mail.'}
                            </Typography>
                            {notVerified.email && (
                                <Typography sx={{ fontSize: '0.8rem', color: '#701280', fontWeight: 600, mb: 1.5 }}>
                                    {notVerified.email}
                                </Typography>
                            )}
                            <Button
                                fullWidth variant="outlined" size="small"
                                onClick={handleResend}
                                disabled={cooldown > 0 || resending}
                                sx={{
                                    borderRadius: '10px',
                                    borderColor: cooldown > 0 ? 'rgba(0,0,0,0.12)' : '#701280',
                                    color: cooldown > 0 ? '#9B9BAB' : '#701280',
                                    fontWeight: 600, fontSize: '0.82rem',
                                    textTransform: 'none',
                                    '&:hover': { borderColor: '#701280', background: 'rgba(112,18,128,0.05)' },
                                    '&.Mui-disabled': { borderColor: 'rgba(0,0,0,0.1)', color: '#aaa' },
                                }}
                            >
                                {resending ? (
                                    <CircularProgress size={16} sx={{ color: '#701280' }} />
                                ) : cooldown > 0 ? (
                                    `${t('registration.resend_wait') || 'Reinvia tra'} ${cooldown}s`
                                ) : (
                                    t('login.resend_verification') || 'Invia una nuova mail di conferma'
                                )}
                            </Button>
                            <Link
                                onClick={() => notVerified.clear()}
                                sx={{ display: 'block', mt: 1.5, fontSize: '0.8rem', color: '#9B9BAB', cursor: 'pointer', textDecoration: 'none', '&:hover': { color: '#6B6B7B' } }}
                            >
                                {t('login.back_to_login') || '← Torna al login'}
                            </Link>
                        </Box>
                    )}

                    {!notVerified.value && (
                        <>
                            <Box sx={{ mb: 3.5 }}>
                                <Typography variant="h2" sx={{ color: '#1C1C28', mb: 0.5, fontWeight: 700, fontSize: '1.75rem' }}>
                                    {t('login.title')}
                                </Typography>
                                <Typography sx={{ color: '#6B6B7B', fontSize: '0.9rem' }}>
                                    {t('login.subtitle')}
                                </Typography>
                            </Box>

                            <Box component="form" onSubmit={(e) => { e.preventDefault(); submit.set(); }} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <TextField
                                    fullWidth label="Email" type="email" name="email"
                                    autoComplete="email" value={email.value}
                                    onChange={(e) => email.set(e.target.value)}
                                    error={errors.value.email} size="small"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EmailIcon sx={{ fontSize: '1.1rem', color: '#6B6B7B' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <TextField
                                    fullWidth label="Password"
                                    type={showPass ? 'text' : 'password'}
                                    name="password" autoComplete="current-password"
                                    value={password.value} onChange={(e) => password.set(e.target.value)}
                                    error={errors.value.password} size="small"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LockIcon sx={{ fontSize: '1.1rem', color: '#6B6B7B' }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setShowPass(!showPass)} sx={{ color: '#6B6B7B' }}>
                                                    {showPass ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <input type="submit" hidden />
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5, mb: 2.5 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={remember.value}
                                            onChange={(e) => remember.set(e.target.checked)}
                                            size="small"
                                            sx={{ color: '#6B6B7B', '&.Mui-checked': { color: '#701280' } }}
                                        />
                                    }
                                    label={<Typography sx={{ fontSize: '0.83rem', color: '#6B6B7B' }}>{t('login.remember')}</Typography>}
                                />
                                <Link
                                    onClick={() => setForgotOpen(true)}
                                    sx={{ fontSize: '0.83rem', color: '#701280', cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                >
                                    {t('login.forgot_password')}
                                </Link>
                            </Box>

                            <Button
                                fullWidth variant="contained" color="primary"
                                onClick={submit.set} disabled={submit.value}
                                sx={{ py: 1.4, fontSize: '0.95rem', mb: 2 }}
                            >
                                {submit.value ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    <>
                                        <AutoAwesomeIcon sx={{ fontSize: '1rem', mr: 0.8 }} />
                                        {t('login.submit')}
                                    </>
                                )}
                            </Button>

                            <Typography sx={{ textAlign: 'center', fontSize: '0.85rem', color: '#6B6B7B', mb: 2.5 }}>
                                {t('login.no_account')}{' '}
                                <Link
                                    onClick={() => navigate('/registration')}
                                    sx={{ color: '#701280', cursor: 'pointer', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                >
                                    {t('login.create_account')}
                                </Link>
                            </Typography>

                        </>
                    )}
                </Box>
            </Box>
        </Box>

        <ForgotPasswordModal open={forgotOpen} onClose={() => setForgotOpen(false)} />
        </>
    );
}

export default Login;
